import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./NextBestActionBlockComponent.css?inline";
import { type AcceptableNextBestAction } from "../types.js";
import "./ClickableBlockComponent.js";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * NextBestActionBlockComponent — A component for rendering a clickable NBA suggestion from the assistant.
 *
 * It renders as a chat bubble that dispatches an 'accept-nba' event when clicked.
 */
export class NextBestActionBlockComponent extends HTMLElement {
    private textSpan: HTMLSpanElement | null = null;
    private wrapper: HTMLElement | null = null;

    static get observedAttributes(): string[] {
        return ["nba", "role"];
    }

    /**
     * Returns the text content for accessibility aggregation.
     */
    get accessibilityText(): string {
        const nbaAttr = this.getAttribute("nba");
        if (nbaAttr) {
            try {
                const nba = JSON.parse(nbaAttr) as AcceptableNextBestAction;
                return nba.text;
            } catch {
                return "";
            }
        }
        return "";
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }
        this.updateContent();
    }

    attributeChangedCallback(name: string): void {
        if (name === "nba" || name === "role") {
            this.updateContent();
            this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
        }
    }

    private render(): void {
        this.wrapper = document.createElement("jt-clickable-block");
        this.updateContent();

        const content = document.createElement("div");
        content.className = "chat-message__nba-content";

        this.textSpan = document.createElement("span");
        this.textSpan.className = "chat-message__text";
        content.appendChild(this.textSpan);
        this.wrapper.appendChild(content);

        this.shadowRoot?.appendChild(this.wrapper);

        this.wrapper.addEventListener("click", () => {
            const nbaAttr = this.getAttribute("nba");
            if (nbaAttr) {
                try {
                    const nba = JSON.parse(nbaAttr) as AcceptableNextBestAction;
                    this.dispatchEvent(new CustomEvent("accept-nba", {
                        detail: nba,
                        bubbles: true,
                        composed: true
                    }));
                } catch {
                    console.error("Failed to parse NBA data for event dispatch");
                }
            }
        });
    }

    private updateContent(): void {
        if (this.wrapper) {
            this.wrapper.setAttribute("role", this.getAttribute("role") || "assistant");
            this.wrapper.setAttribute("icon", "send-solid");
        }
        if (this.textSpan) {
            this.textSpan.textContent = this.accessibilityText;
        }
    }
}

if (!customElements.get("jt-next-best-action-block")) {
    customElements.define("jt-next-best-action-block", NextBestActionBlockComponent);
}
